import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faFilter } from "@fortawesome/free-solid-svg-icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "../../style/DonorInventory.css";

const TopReceiversChart = ({
  topReceivers,
  receiversByMaterial,
  availableMaterials,
  darkMode,
}) => {
  const { t } = useTranslation();
  const [selectedMaterial, setSelectedMaterial] = useState("all");

  // get the data to display based on filter
  const displayData = useMemo(() => {
    if (selectedMaterial === "all") {
      return topReceivers.map((r) => ({
        name: r.company,
        quantity: r.quantity,
        revenue: r.revenue,
        allocations: r.allocations,
        city: r.city,
      }));
    }

    const materialReceivers = receiversByMaterial[selectedMaterial] || [];
    return materialReceivers.map((r) => ({
      name: r.company,
      quantity: r.quantity,
      revenue: 0, // not available per-material
      allocations: 0,
      city: "",
    }));
  }, [selectedMaterial, topReceivers, receiversByMaterial]);

  // theme colors
  const axisColor = darkMode ? "#aaaaaa" : "#666666";
  const gridColor = darkMode ? "#444444" : "#e0e0e0";
  const barColor = "#669d2e";

  // custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{data.name}</p>
          {data.city && <p className="chart-tooltip-subtitle">{data.city}</p>}
          <p>{data.quantity.toLocaleString()} lbs</p>
          {data.revenue > 0 && (
            <p>
              $
              {data.revenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </p>
          )}
          {data.allocations > 0 && (
            <p>
              {data.allocations} {t("allocations")}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // cursor style for hover
  const cursorStyle = {
    fill: darkMode ? "rgba(102, 157, 46, 0.25)" : "rgba(102, 157, 46, 0.12)",
  };

  // calculate chart height based on data
  const chartHeight = Math.max(300, displayData.length * 45);

  return (
    <div className="top-receivers-section">
      <div className="chart-card chart-card--full">
        <div className="chart-card-header">
          <div className="chart-card-icon blue">
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <h5 className="chart-card-title">{t("Top Receivers")}</h5>

          {/* Material Filter */}
          <div className="chart-filter">
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            <select
              value={selectedMaterial}
              onChange={(e) => setSelectedMaterial(e.target.value)}
              className="chart-filter-select"
            >
              <option value="all">{t("All Materials")}</option>
              {availableMaterials.map((mat) => (
                <option key={mat} value={mat}>
                  {mat}
                </option>
              ))}
            </select>
          </div>

          {displayData.length > 0 && (
            <span className="chart-card-badge">
              {displayData.length} {t("receivers")}
            </span>
          )}
        </div>

        <div className="chart-card-content">
          {displayData.length === 0 ? (
            <div className="chart-empty-state">
              <FontAwesomeIcon icon={faBuilding} className="empty-icon" />
              <p>{t("No receiver data for selected filters")}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={displayData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={gridColor}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  stroke={axisColor}
                  fontSize={11}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke={axisColor}
                  fontSize={11}
                  width={150}
                  tickFormatter={(value) =>
                    value.length > 20 ? `${value.substring(0, 18)}...` : value
                  }
                />
                <Tooltip content={<CustomTooltip />} cursor={cursorStyle} />
                <Bar
                  dataKey="quantity"
                  fill={barColor}
                  name={t("Quantity (lbs)")}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={35}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Summary stats */}
        {displayData.length > 0 && selectedMaterial === "all" && (
          <div className="chart-summary">
            <div className="summary-item">
              <span className="summary-label">{t("Total Distributed")}</span>
              <span className="summary-value">
                {displayData
                  .reduce((sum, r) => sum + r.quantity, 0)
                  .toLocaleString()}{" "}
                lbs
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t("Total Revenue")}</span>
              <span className="summary-value">
                $
                {displayData
                  .reduce((sum, r) => sum + r.revenue, 0)
                  .toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TopReceiversChart.propTypes = {
  topReceivers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      company: PropTypes.string.isRequired,
      city: PropTypes.string,
      quantity: PropTypes.number.isRequired,
      revenue: PropTypes.number,
      allocations: PropTypes.number,
    })
  ).isRequired,
  receiversByMaterial: PropTypes.object,
  availableMaterials: PropTypes.arrayOf(PropTypes.string),
  darkMode: PropTypes.bool,
};

TopReceiversChart.defaultProps = {
  receiversByMaterial: {},
  availableMaterials: [],
};

export default TopReceiversChart;
